import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const ANIM_MS = 220;

const Popup = ({ open, title, children, onClose, onConfirm, confirmText, cancelText, showConfirm = false, footerActions = null }) => {
  const { t } = useTranslation();
  const textoConfirmar = confirmText || t('comun.confirmar');
  const textoCancelar = cancelText || t('comun.cerrar');
  const [shouldRender, setShouldRender] = useState(open);
  const [exiting, setExiting] = useState(false);
  const cardRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  useEffect(() => {
    let t;
    if (open) {
      // Mount and ensure enter animation
      setShouldRender(true);
      setExiting(false);
    } else if (shouldRender) {
      // Start exit animation then unmount
      setExiting(true);
      t = setTimeout(() => {
        setShouldRender(false);
        setExiting(false);
      }, ANIM_MS);
    }
    return () => clearTimeout(t);
  }, [open]);

  // Ajustar la altura del body para que sea el único elemento con scroll
  useEffect(() => {
    const adjust = () => {
      const card = cardRef.current;
      const header = headerRef.current;
      const body = bodyRef.current;
      const footer = footerRef.current;
      if (!card || !body) return;

      // altura máxima del card (coincide con CSS: 85vh)
      const maxCardH = Math.min(window.innerHeight * 0.85, card.clientHeight || window.innerHeight * 0.85);
      const headerH = header ? header.offsetHeight : 0;
      const footerH = footer ? footer.offsetHeight : 0;
      // dejar espacio para paddings
      const padding = 32; // aproximado: top+bottom padding en .popup-body
      const available = Math.max(80, Math.floor(maxCardH - headerH - footerH - padding));
      body.style.maxHeight = `${available}px`;
      body.style.overflow = 'auto';
    };

    if (shouldRender) {
      // ajustar ahora y al redimensionar
      adjust();
      window.addEventListener('resize', adjust);
      // también ajustar después de una pequeña espera por si el contenido cambia (imagenes, etc.)
      const t = setTimeout(adjust, 100);
      return () => {
        window.removeEventListener('resize', adjust);
        clearTimeout(t);
      };
    }
  }, [shouldRender, children]);

  if (!shouldRender) return null;

  const handleBackground = (e) => {
    if (e.target.classList.contains('popup-overlay')) onClose();
  };

  return (
    <div className={`popup-overlay ${exiting ? 'popup-exit' : ''}`} onMouseDown={handleBackground}>
      <div className={`popup-card ${exiting ? 'popup-exit' : ''}`} role="dialog" aria-modal="true" ref={cardRef}>
        <div className="popup-header" ref={headerRef}>
          <h3>{title}</h3>
          <button className="popup-close" onClick={onClose} aria-label={t('comun.cerrar')}>×</button>
        </div>
        <div className="popup-body" ref={bodyRef}>{children}</div>
        <div className="popup-footer" ref={footerRef}>
          {/* Custom actions provided by consumer (e.g. Crear usuario) */}
          {footerActions}
          {/* Default cancel/confirm buttons */}
          <button className="popup-btn popup-cancel" onClick={onClose}>{textoCancelar}</button>
          {showConfirm && <button className="popup-btn popup-confirm" onClick={onConfirm}>{textoConfirmar}</button>}
        </div>
      </div>
    </div>
  );
};

export default Popup;
